/**
 * Copy Button
 *
 * Shared clipboard button for markdown, HTML, and exact text copy actions.
 *
 * Primary responsibilities:
 * - Copy text or rendered markdown to the clipboard
 * - Show copied state feedback
 * - Notify callers after a copy action
 *
 * Dependencies:
 * - Clipboard hook
 * - Button with tooltip
 */

import { IconCheck, IconClipboard } from '@tabler/icons-react'
import katex from 'katex'
import MarkdownIt from 'markdown-it'

import { useCopyToClipboard } from '@/lib/hooks/use-copy-to-clipboard'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'

import { ButtonWithTooltip } from './button-with-tooltip'

export function CopyButton({
  value,
  title = 'Copy to clipboard',
  variant = 'link',
  className,
  exact = false,
  onCopied
}: {
  value: string
  variant?: 'link' | 'outline'
  title?: string
  className?: string
  exact?: boolean
  onCopied?: () => void
}) {
  const { isCopied, copyToClipboard } = useCopyToClipboard({ timeout: 2000 })

  const renderLatex = (latex: string, displayMode: boolean): string => {
    return katex.renderToString(latex, {
      displayMode: displayMode,
      throwOnError: false
    })
  }

  const handleCopy = () => {
    if (exact) {
      copyToClipboard(value, { exact: true })
      onCopied?.()
      return
    }

    const md = new MarkdownIt({
      html: true,
      breaks: true,
      linkify: true
    })

    let plainTextContent = value
    let htmlContent = value

    // Extract content from <o> tag or use the entire content if no tags present
    const outputRegex = /<o>([\s\S]*?)<\/output>/g
    const outputMatches = [...plainTextContent.matchAll(outputRegex)]

    if (outputMatches.length > 0) {
      plainTextContent = outputMatches.map((match) => match[1]).join('\n\n')
      htmlContent = plainTextContent
    }

    const convertBlockLatexToText = (latex: string): string => {
      return latex
        .replace(/\\text\{([^}]+)\}/g, '$1')
        .replace(/\\cdot/g, '·')
        .replace(/\\times/g, '*')
        .replace(/\\frac\{([^}]+)\}\{([^}]+)\}/g, '$1/$2')
        .replace(/\s+/g, ' ')
        .trim()
    }

    // Handle block LaTeX
    htmlContent = plainTextContent.replace(/\$\$([\s\S]*?)\$\$/g, (match, latex) => {
      htmlContent = htmlContent.replace(match, renderLatex(latex, true))
      return convertBlockLatexToText(latex) // Convert LaTeX to text without delimiters
    })

    // Render the rest of the markdown
    htmlContent = md.render(htmlContent)

    // This will automatically handle transforming interactive components
    copyToClipboard(plainTextContent, htmlContent)
    onCopied?.()
  }

  return (
    <ButtonWithTooltip
      tooltip={title}
      size={'icon'}
      className={cn('size-4 hover:opacity-50', className)}
      variant={variant}
      onClick={() => {
        if (isCopied) return
        handleCopy()
      }}
    >
      {isCopied ? <IconCheck stroke={1.5} size={16} /> : <IconClipboard stroke={1.5} size={16} />}
    </ButtonWithTooltip>
  )
}
